import { useState, useCallback } from 'react';

// Constants
const DEFAULT_COUNTRY = "CA";

/**
 * IP-based location detection as fallback
 * @returns {Promise<string>} Country code or DEFAULT_COUNTRY
 */
const detectCountryByIP = async () => {
    try {
        const response = await fetch('https://ipapi.co/json/', {
            timeout: 5000
        });

        if (!response.ok) {
            throw new Error('IP location service unavailable');
        }

        const data = await response.json();
        return data.country_code || DEFAULT_COUNTRY;
    } catch (error) {
        console.warn("IP-based location detection failed:", error);
        return DEFAULT_COUNTRY;
    }
};

/**
 * Custom hook for geolocation functionality
 * Handles country detection based on user's location with multiple fallback methods
 * @returns {Object} Object containing detectCountry function, loading state, and error state
 */
export const useGeolocation = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const detectLocation = useCallback(async () => {
        if (!navigator.geolocation) {
            console.warn("Geolocation not supported by this browser");
            return await detectLocationByIP();
        }

        setIsLoading(true);
        setError(null);

        try {
            const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    timeout: 8000,
                    enableHighAccuracy: false,
                    maximumAge: 600000
                });
            });

            const { latitude, longitude } = position.coords;
            const response = await fetch(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
                { timeout: 5000 }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch location data');
            }

            const data = await response.json();
            return {
                countryCode: data.countryCode || DEFAULT_COUNTRY,
                city: data.city || data.locality || ""
            };
        } catch (err) {
            console.warn("Geolocation error:", err);
            
            // Specifically identify permission denial (code 1)
            if (err.code === 1) {
                throw err; // Let the component handle denial alert
            }

            // Fallback for other errors (timeout, position unavailable, etc)
            return await detectLocationByIP();
        } finally {
            setIsLoading(false);
        }
    }, []);

    return { detectLocation, isLoading, error };
};

/**
 * IP-based location detection as fallback for all fields
 */
const detectLocationByIP = async () => {
    try {
        const response = await fetch('https://ipapi.co/json/', { timeout: 5000 });
        if (!response.ok) throw new Error('IP service unavailable');
        const data = await response.json();
        return {
            countryCode: data.country_code || DEFAULT_COUNTRY,
            city: data.city || ""
        };
    } catch (error) {
        console.warn("IP-based location detection failed:", error);
        return { countryCode: DEFAULT_COUNTRY, city: "" };
    }
};


/**
 * Standalone function to detect country without React hooks
 * Useful for non-React contexts or when you don't need state management
 * Includes IP-based fallback for CoreLocation errors
 * @returns {Promise<string>} Country code or DEFAULT_COUNTRY
 */
export const detectCountryAsync = async () => {
    if (!navigator.geolocation) {
        console.warn("Geolocation not supported by this browser");
        return await detectCountryByIP();
    }

    try {
        const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
                timeout: 6000,
                enableHighAccuracy: false,
                maximumAge: 600000
            });
        });

        const { latitude, longitude } = position.coords;
        const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
            { timeout: 5000 }
        );

        if (!response.ok) {
            throw new Error('Failed to fetch location data');
        }

        const data = await response.json();
        return data.countryCode || DEFAULT_COUNTRY;
    } catch (err) {
        console.warn("Geolocation error:", err);

        if (err.code) {
            switch (err.code) {
                case 1:
                    console.warn("User denied geolocation permission");
                    break;
                case 2:
                    console.warn("Position information is unavailable (CoreLocation error)");
                    break;
                case 3:
                    console.warn("Geolocation request timed out");
                    break;
                default:
                    console.warn("Unknown geolocation error");
            }
        }

        // Try IP-based detection as fallback
        console.log("Attempting IP-based location detection as fallback...");
        return await detectCountryByIP();
    }
};

export { DEFAULT_COUNTRY };
